'use client';

import { useMemo } from 'react';

import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Theme } from '@/common/enums';
import { useConfigValue } from '@/components/providers/config-provider';
import { useTheme } from '@/components/providers/theme-provider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const t = useTranslations('headerActions.theme');

  const enableThemeToggle = useConfigValue<boolean>('meta.enableThemeToggle') ?? false;
  const defaultTheme = (useConfigValue('meta.defaultTheme') as Theme) || Theme.Light;

  const currentTheme = theme || defaultTheme;

  const getThemeIcon = useMemo(() => {
    switch (currentTheme) {
      case Theme.Light:
        return <Sun className="size-4" />;
      case Theme.Dark:
        return <Moon className="size-4" />;
      case Theme.System:
        return <Monitor className="size-4" />;
      default:
        return <Sun className="size-4" />;
    }
  }, [currentTheme]);

  const getThemeLabel = useMemo(() => {
    switch (currentTheme) {
      case Theme.Light:
        return t('light');
      case Theme.Dark:
        return t('dark');
      case Theme.System:
        return t('system');
      default:
        return t('light');
    }
  }, [currentTheme, t]);

  const themes = [
    { value: Theme.Light, label: t('light'), icon: <Sun className="size-4" /> },
    { value: Theme.Dark, label: t('dark'), icon: <Moon className="size-4" /> },
    { value: Theme.System, label: t('system'), icon: <Monitor className="size-4" /> },
  ];

  if (!enableThemeToggle) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-7 gap-1.5 px-2 text-xs font-medium"
          aria-label={t('label')}
        >
          {getThemeIcon}
          <span>{getThemeLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {themes.map(themeOption => (
          <DropdownMenuItem
            key={themeOption.value}
            onClick={() => setTheme(themeOption.value)}
            className="gap-2"
          >
            {themeOption.icon}
            <span className={currentTheme === themeOption.value ? 'font-semibold' : ''}>
              {themeOption.label}
            </span>
            {currentTheme === themeOption.value && <span className="ms-auto">✓</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
